import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';

import { IQuestion, IQuestionResponse } from '../pages/check-lists-page/interfaces/question.interface';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { IChecklist } from '../pages/check-lists-page/interfaces/checklist.interface';
import { IQuestionCategory } from '../pages/check-lists-page/interfaces/question-category.interface';
import { ListOf } from '../pages/check-lists-page/interfaces/list-of.interface';
import { ISaveChecklist } from '../pages/check-lists-page/interfaces/save-checklist.interface';
import { IDeleteQuestionError, IDeleteQuestionsResponse } from '../pages/check-lists-page/interfaces/delete-questions-response.interface';
import { ICreateChecklistParams } from '../pages/check-lists-page/interfaces/create-checklist-params.interface';
import { IChecklistTicket, IDepartureTicket } from '../pages/check-lists-page/interfaces/checklist-ticket.interface';
import { IChecklistDeparturesParams, IChecklistTicketsParams } from '../pages/check-lists-page/interfaces/checklist-ticket-request.interface';
import { ISectorNumber } from '../pages/check-lists-page/interfaces/sector-number';
import { BranchNames } from '../pages/sectors/sectors';
import { IChecklistTicketAnswer } from '../pages/check-lists-page/interfaces/checklist-ticket-answer.interface';
import { ITicketType } from '../pages/check-lists-page/interfaces/ticket-type.interface';
import { IGlobalProblemSubtype } from '../pages/check-lists-page/interfaces/global-problem-subtype.interface';
import { ChecklistPdfGeneratorService } from '../pages/check-lists-page/services/checklist-pdf-generator.service';

@Injectable({
  providedIn: 'root',
})
export class CheckListsApiService {
  constructor(
    private http: HttpClient,
    private pdfGeneratorService: ChecklistPdfGeneratorService
  ) {}

  getTicketTypes(): Observable<ITicketType[]> {
    return this.http.get<{ data: ITicketType[] }>(`/admin/ticket_types/`).pipe(map((res) => res.data));
  }

  getChecklist(ticketType: string): Observable<IChecklist> {
    return this.http.get<{ data: IChecklist }>(`checklist/checklists/${{ ticketType }}/`).pipe(map((res) => res.data));
  }

  getHDTicketTypes<T>(): Observable<T> {
    return this.http.get<{ data: T }>('/admin/hd_ticket_types/esas_local').pipe(map(({ data }) => data));
  }

  getChecklists<T>(ticketType?: string): Observable<T> {
    const url = ticketType ? `/checklist/checklists/${ticketType}` : '/checklist/checklists';

    return this.http.get<{ data: T }>(url).pipe(map(({ data }: { data: T }) => data));
  }

  createChecklist<T extends IChecklist>(params: ICreateChecklistParams): Observable<T> {
    return this.http.post<T>('/checklist/checklists/', params);
  }

  saveChecklistQuestions(ticketType: string, saveChecklists: ISaveChecklist[]): Observable<IChecklist> {
    return this.http.put<IChecklist>(`/checklist/checklists/${ticketType}/save_questions/`, saveChecklists);
  }

  saveChecklist(ticketType: string, checklist: Partial<IChecklist>) {
    return this.http.put<IChecklist>(`/checklist/checklists/${ticketType}/`, checklist);
  }

  createQuestion(request: Partial<IQuestion>): Observable<IQuestion> {
    return this.http.post<{ data: IQuestion }>('/checklist/questions/', request).pipe(map(({ data }) => data));
  }

  createCategory(name: string): Observable<IQuestionCategory> {
    return this.http
      .post<{ data: IQuestionCategory }>('/checklist/question-categories/', {
        name,
      })
      .pipe(map(({ data }) => data));
  }

  deleteQuestions(questionIds: number[]): Observable<IDeleteQuestionError[]> {
    return this.http
      .post<IDeleteQuestionsResponse>('/checklist/questions/delete_list', {
        q_list: questionIds,
      })
      .pipe(map(({ data }) => data.errors));
  }

  deleteQuestion(questionId: number): Observable<void> {
    return this.http.delete<void>(`/checklist/questions/${questionId}`);
  }

  getQuestionCategories(): Observable<IQuestionCategory[]> {
    return this.http.get<{ data: IQuestionCategory[] }>('/checklist/question-categories/').pipe(map(({ data }) => data));
  }

  getQuestion(questionId: number): Observable<IQuestion> {
    return this.http.get<{ data: IQuestion }>(`/checklist/questions/${questionId}/`).pipe(map(({ data }) => data));
  }

  getQuestions({ category, search, limit, offset }): Observable<IQuestionResponse> {
    let params = new HttpParams().set('category', category).set('search', search).set('limit', limit).set('offset', offset);

    return this.http.get<ListOf<any>>('/checklist/questions', { params }).pipe(
      map((response) => ({
        count: response.data.count,
        results: response.data.results,
      }))
    );
  }

  getTickets(ticketsParams: IChecklistTicketsParams): Observable<{ results: IChecklistTicket[]; count: number }> {
    const params = new HttpParams({
      fromObject: { ...(ticketsParams as Record<string, string>) },
    });

    return this.http.get<{ results: IChecklistTicket[]; count: number }>('/checklist/tickets', {
      params,
    });
  }

  getDepartures(ticketsParams: IChecklistDeparturesParams): Observable<{ results: IDepartureTicket[]; count: number }> {
    const params = new HttpParams({
      fromObject: { ...(ticketsParams as Record<string, string>) },
    });

    return this.http.get<{ results: IDepartureTicket[]; count: number }>('/checklist/departures/', {
      params,
    });
  }

  getAnswers(ticketId: number): Observable<{ data: IChecklistTicketAnswer[] }> {
    return this.http.get<{ data: IChecklistTicketAnswer[] }>(`/checklist/ticket/${ticketId}/answers/`);
  }

  getSectors(): Observable<ISectorNumber[]> {
    return this.http.get<{ data: ISectorNumber[] }>(`/admin/sector_numbers`).pipe(map((response) => response.data));
  }

  getBranches(): Observable<BranchNames[]> {
    return this.http.get<{ data: BranchNames[] }>(`/admin/branches`).pipe(map((response) => response.data));
  }

  downloadMedia(hdTicketNumber: number): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`/checklist/ticket-${hdTicketNumber}-images.zip`, {
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const contentDisposition = response.headers.get('content-disposition');
          const filename = contentDisposition.match(/filename="(.+)?"/)[1];
          saveAs(response.body, filename);
        })
      );
  }

  downloadMediaGRRS(departureId: number): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`/checklist/departure-${departureId}-images.zip`, {
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const contentDisposition = response.headers.get('content-disposition');
          const filename = contentDisposition.match(/filename="(.+)?"/)[1];
          saveAs(response.body, filename);
        })
      );
  }

  exportToCsv(ticketIds: number[]): Observable<HttpResponse<Blob>> {
    const params = new HttpParams({
      fromObject: { hd_ticket_number: ticketIds.map(String) },
    });

    return this.http
      .get('/checklist/tickets/download-csv', {
        params,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const contentDisposition = response.headers.get('content-disposition');
          const filename = contentDisposition.match(/filename="(.+)?"/)[1];
          const blob = new Blob([response.body], {
            type: 'text/csv;charset=utf-8',
          });
          saveAs(blob, filename);
        })
      );
  }

  exportToCsvGRRS(ticketIds: number[]): Observable<HttpResponse<Blob>> {
    const params = new HttpParams({
      fromObject: { id: ticketIds.map(String) },
    });

    return this.http
      .get('/checklist/departures/download-csv', {
        params,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const contentDisposition = response.headers.get('content-disposition');
          const filename = contentDisposition.match(/filename="(.+)?"/)[1];
          const blob = new Blob([response.body], {
            type: 'text/csv;charset=utf-8',
          });
          saveAs(blob, filename);
        })
      );
  }

  downloadPdfFromCsv(ticket: IChecklistTicket): Observable<HttpResponse<Blob>> {
    const params = new HttpParams({
      fromObject: { hd_ticket_number: [String(ticket.hd_ticket_number)] },
    });

    return this.http
      .get('/checklist/tickets/download-csv', {
        params,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        switchMap((response: HttpResponse<Blob>) =>
          from(this.pdfGeneratorService.generateAndSavePdf(response.body, ticket)).pipe(map(() => response))
        )
      );
  }

  downloadPdfFromCsvGRRS(ticket: IDepartureTicket): Observable<HttpResponse<Blob>> {
    const params = new HttpParams({
      fromObject: { id: [String(ticket.id)] },
    });

    return this.http
      .get('/checklist/departures/download-csv', {
        params,
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        switchMap((response: HttpResponse<Blob>) =>
          from(this.pdfGeneratorService.generateAndSavePdf(response.body, ticket)).pipe(map(() => response))
        )
      );
  }

  getGPSubtypes(): Observable<IGlobalProblemSubtype[]> {
    return this.http.get<{ data: IGlobalProblemSubtype[] }>('/helpdesk/global_problem_subtypes').pipe(map((res) => res.data));
  }
}
